import { buildRowLookup, resolveRow } from './matrixRowLookup';
import formatMomentLabel from '../formatMomentLabel';
import mirrorScore from './mirrorScore';
import SUSPECT_THRESHOLDS from './suspectSubmissionThresholds';

/**
 * detectSuspectSubmissions — invullingen die het gemiddelde vertekenen
 *
 * Drie signalen over één vraag, allemaal puur uit te rekenen over de records
 * die de diepte-analyse toch al ophaalt. Er wordt niets gefilterd en niets
 * opgeslagen: de uitkomst is alleen bedoeld om te tonen.
 *
 * 1. `notRead` — elke rij dezelfde ruwe waarde terwijl de vraag een
 *    controlevraag bevat. Intern tegenstrijdig: "ik kan me niet verstaanbaar
 *    maken" kan niet samen met "ik spreek slecht Nederlands — oneens".
 * 2. `controlMismatch` — de gespiegelde controlevraag ligt ver van de rest,
 *    zonder dat alle antwoorden gelijk zijn. Zelfde soort bewijs, subtieler.
 * 3. `outliers` — het gespiegelde gemiddelde ligt ver van de mediaan binnen
 *    hetzelfde meetmoment. NADRUKKELIJK GEEN FOUT: een lage score betekent
 *    meestal dat iemand het zwaar heeft. De afnemer hoort dit anders te tonen
 *    dan de eerste twee.
 *
 * De valentie komt uit `posNeg` van de rij, nooit uit het verschil tussen ruw
 * en gespiegeld — midden op de schaal zijn die identiek (1 + 5 − 3 = 3), en
 * dat is juist de meest voorkomende vorm.
 *
 * @typedef {object} SuspectAnswer
 * @property {string} rowId - RowID van de rij ('' als het document dat niet draagt).
 * @property {string} title - Rijtitel.
 * @property {number} raw - Ruw antwoord.
 * @property {number} mirrored - Antwoord na spiegeling.
 * @property {boolean} control - Of dit de controlevraag is.
 *
 * @typedef {object} SuspectSubmission
 * @property {string} key - Stabiele sleutel (invulling × vraag).
 * @property {string} formId - De invulling.
 * @property {string} fieldId - De vraag.
 * @property {string} persona - Deelnemer-id ('' bij anoniem).
 * @property {string} momentLabel - Leesbaar meetmoment.
 * @property {SuspectAnswer[]} answers - Alle antwoorden van deze invulling op deze vraag.
 * @property {number} [value] - Bij `notRead`: de waarde die overal is ingevuld.
 * @property {number} [gap] - Bij `controlMismatch`: het verschil in punten.
 * @property {number} [mean] - Bij `outliers`: het gespiegelde gemiddelde.
 * @property {number} [median] - Bij `outliers`: de mediaan van de groep.
 *
 * @typedef {object} SuspectCoverage
 * @property {number} submissions - Alle invulling × vraag-combinaties die zijn bekeken.
 * @property {number} checked - Daadwerkelijk getoetst op de harde signalen.
 * @property {number} noControlRow - Overgeslagen: de vraag heeft geen controlevraag.
 * @property {number} noControlAnswer - Overgeslagen: deze invulling beantwoordde de controlevraag niet.
 * @property {number} tooFewRows - Overgeslagen: minder dan MIN_ROWS beantwoorde rijen.
 * @property {number} noFormId - Overgeslagen antwoorden zonder invulling-id.
 *
 * @typedef {object} SuspectResult
 * @property {SuspectSubmission[]} notRead
 * @property {SuspectSubmission[]} controlMismatch
 * @property {SuspectSubmission[]} outliers
 * @property {SuspectCoverage} coverage
 */

const median = (values) => {
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Positie van een record, of null als het meetmoment niet te plaatsen is. */
const positionOf = (record, id2pos) => {
    let position = record?.MomentPosition;
    if (position == null) position = id2pos[record?.MomentID];
    if (typeof position !== 'number') position = parseInt(position, 10);
    return Number.isNaN(position) ? null : position;
};

/**
 * @param {object} params
 * @param {Array<object>} [params.records] - Response-records (hierarchy-verrijkt).
 * @param {Array<object>} [params.rows] - MatrixQuestionRows van deze vraag (soft-delete gefilterd door de caller).
 * @param {object} [params.field] - QuestionnaireFields-doc, voor ReachStart/ReachEnd.
 * @param {Object<string, number>} [params.id2pos] - MomentID → Position.
 * @param {Object<number, string>} [params.pos2title] - Position → Titel.
 * @returns {{notRead: SuspectSubmission[], controlMismatch: SuspectSubmission[], outliers: SuspectSubmission[], coverage: SuspectCoverage}}
 */
const detectSuspectSubmissions = ({
    records, rows, field, id2pos = {}, pos2title = {},
} = {}) => {
    const leeg = {
        notRead: [], controlMismatch: [], outliers: [],
        coverage: {
            submissions: 0, checked: 0, noControlRow: 0,
            noControlAnswer: 0, tooFewRows: 0, noFormId: 0,
        },
    };
    const list = Array.isArray(records) ? records : [];
    if (!list.length) return leeg;

    const rowList = Array.isArray(rows) ? rows : [];
    const lookup = buildRowLookup(rowList);
    const scale = {
        lowerScale: field?.ReachStart ?? 0,
        upperScale: field?.ReachEnd ?? 5,
    };

    // Welke vragen dragen een controlevraag? Zonder één is 'overal hetzelfde'
    // legitiem en valt er niets te toetsen.
    const fieldsWithControl = new Set();
    rowList.forEach((row) => {
        if (row?.Field && row.posNeg === 'negative') fieldsWithControl.add(row.Field);
    });

    /** @type {Map<string, {formId: string, fieldId: string, persona: string, position: number|null, answers: SuspectAnswer[]}>} */
    const submissions = new Map();
    let noFormId = 0;

    list.forEach((record) => {
        if (record?.Input === '' || record?.Input == null) return;
        if (!Number.isFinite(Number(record.Input))) return;
        if (!record.FormID) { noFormId += 1; return; }
        if (!record.FieldID) return;

        const def = resolveRow(lookup, record);
        const isControl = !!def && def.posNeg === 'negative';
        const key = `${record.FormID}|${record.FieldID}`;
        if (!submissions.has(key)) {
            submissions.set(key, {
                formId: record.FormID,
                fieldId: record.FieldID,
                persona: record.Persona && record.Persona !== 'undefined' ? record.Persona : '',
                position: positionOf(record, id2pos),
                answers: [],
            });
        }
        submissions.get(key).answers.push({
            rowId: record.RowID || (def && def.ID) || '',
            title: (def && def.Title) || record.RowTitle || '',
            raw: Number(record.Input),
            mirrored: mirrorScore(record.Input, scale, isControl),
            control: isControl,
        });
    });

    const notRead = [];
    const controlMismatch = [];
    /** @type {Map<number|string, Array<{key: string, mean: number, sub: object}>>} */
    const byPosition = new Map();
    // Eén teller per reden. Alles op één teller boeken laat de detectie kloppen
    // maar maakt de uitleg op het scherm onwaar, en verbergt het beleid.
    // AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
    const skipped = { noControlRow: 0, noControlAnswer: 0, tooFewRows: 0 };
    let checked = 0;

    submissions.forEach((sub, key) => {
        const basis = {
            key,
            formId: sub.formId,
            fieldId: sub.fieldId,
            persona: sub.persona,
            momentLabel: sub.position == null
                ? 'Onbekend meetmoment'
                : formatMomentLabel(pos2title[sub.position], sub.position),
            answers: sub.answers,
        };

        // Uitschieters gaan over het gemiddelde en hebben geen controlevraag
        // nodig — die bak wordt dus altijd gevuld.
        const mirroredValues = sub.answers.map((a) => a.mirrored).filter((v) => v != null);
        if (mirroredValues.length) {
            const mean = mirroredValues.reduce((a, b) => a + b, 0) / mirroredValues.length;
            const bucket = sub.position == null ? '__none__' : sub.position;
            if (!byPosition.has(bucket)) byPosition.set(bucket, []);
            byPosition.get(bucket).push({ key, mean, sub: basis });
        }

        if (!fieldsWithControl.has(sub.fieldId)) { skipped.noControlRow += 1; return; }
        if (sub.answers.length < SUSPECT_THRESHOLDS.MIN_ROWS) { skipped.tooFewRows += 1; return; }
        if (!sub.answers.some((a) => a.control)) { skipped.noControlAnswer += 1; return; }
        checked += 1;

        // 1. Overal dezelfde ruwe waarde.
        const rawValues = new Set(sub.answers.map((a) => a.raw));
        if (rawValues.size === 1) {
            notRead.push({ ...basis, value: sub.answers[0].raw });
            return; // nooit twee keer melden
        }

        // 2. Controlevraag die de rest tegenspreekt.
        const controls = sub.answers.filter((a) => a.control && a.mirrored != null);
        const others = sub.answers.filter((a) => !a.control && a.mirrored != null);
        if (!controls.length || !others.length) return;
        const controlMean = controls.reduce((a, b) => a + b.mirrored, 0) / controls.length;
        const otherMean = others.reduce((a, b) => a + b.mirrored, 0) / others.length;
        const gap = Math.abs(controlMean - otherMean);
        if (gap >= SUSPECT_THRESHOLDS.CONTROL_GAP) controlMismatch.push({ ...basis, gap });
    });

    // 3. Uitschieters per meetmoment, via mediaan + mediane absolute afwijking.
    // Robuust: één extreme waarde verschuift de mediaan niet, een gemiddelde wel.
    const outliers = [];
    byPosition.forEach((entries) => {
        if (entries.length < SUSPECT_THRESHOLDS.OUTLIER_MIN_SUBMISSIONS) return;
        const means = entries.map((e) => e.mean);
        const med = median(means);
        const mad = median(means.map((m) => Math.abs(m - med)));
        // Ondergrens: bij een perfect uniforme groep is de mediane absolute
        // afwijking 0, en dan zou élk verschil een uitschieter heten.
        const spread = Math.max(mad, SUSPECT_THRESHOLDS.OUTLIER_MIN_MAD);
        entries.forEach((entry) => {
            if (Math.abs(entry.mean - med) > SUSPECT_THRESHOLDS.OUTLIER_MAD_FACTOR * spread) {
                outliers.push({ ...entry.sub, mean: entry.mean, median: med });
            }
        });
    });

    return {
        notRead,
        controlMismatch,
        outliers,
        coverage: { submissions: submissions.size, checked, ...skipped, noFormId },
    };
};

export default detectSuspectSubmissions;
